import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AppIcon from './AppIcon';
import { Skeleton } from './ui/Skeleton';
import {
  findNavEntryByPath,
  isDemoModuleId,
  isDemoPagePath,
  normalizeNavPath,
  pickLandingPath,
} from './layout/navigationModules';
import { useNavConfig } from '../api/Navigation/moduleConfig';
import type { FilteredNavigationState } from '../hooks/useFilteredNavigation';

interface RootState {
  user: {
    isLoggedIn: boolean;
    userId: string;
  };
}

interface PageConfigGuardProps {
  children: React.ReactNode;
  /**
   * The already role- and config-filtered navigation, i.e. exactly what the sidebar draws. The guard
   * needs it to choose a redirect target that is REACHABLE — see `resolveEscapePath` below. Passed in
   * rather than re-derived so the guard and the sidebar can never disagree about what is enabled.
   */
  navigation: FilteredNavigationState;
  /**
   * Block the current route even though no config row says so. Used by MasterLayout for the
   * modules Mowazaf Connect never showcases (owner request 2026-10-05, `CONNECT_HIDDEN_MODULE_SLUGS`
   * in data/brochureModules.ts): a Connect tenant typing one of their URLs gets the SAME notice and
   * redirect as a page an Operator switched off, instead of the LockedShell / brochure upsell.
   * Tier-derived and computed live by the caller, so it never writes config and never survives an
   * upgrade. Unlike a config row it also covers paths outside the nav registry (`/brochure/<slug>`,
   * recruitment alias routes), because the caller has already matched them.
   */
  forceBlocked?: boolean;
}

// Matches RoleProtectedRoute, so the two guards feel like one behaviour rather than two.
const REDIRECT_DELAY_MS = 2200;

/**
 * The ONE path this guard must never block. `resolveEscapePath` falls back to it when the filtered
 * navigation yields no candidate at all, and unlike the other two candidates it does NOT come from
 * navigation that has already had module and page config applied -- so the invariant documented
 * above is false for this branch alone. See CO-502.
 */
export const selfProfilePath = (userId: string): string => `/profile/${userId}/personal`;

/**
 * Route guard that enforces the Operator's per-company module and page switches.
 *
 * 🔴 WHAT THIS FIXES. The switches were NAVIGATION-ONLY. `getPageConfig()` had exactly two consumers —
 * `useFilteredNavigation` (sidebar) and `usePageTabConfig` (tab strips) — and no route guard read
 * either config: `RoleProtectedRoute` checks role and nothing else. So a page an Operator had "turned
 * off" still rendered in full whenever the sidebar was bypassed: a typed URL, a bookmark, a link in an
 * older email, browser history, or an AI-assistant deep link (`NavigationCatalog` returns routes
 * without consulting company config). Hiding a menu entry was never the same as making a page
 * unreachable, and anything relying on the toggle to withhold a surface was relying on something the
 * code did not do.
 *
 * Mounted ONCE around `MasterLayout`'s outlet rather than per route, so a route added later is covered
 * by default. A guard that must be remembered on every new route is a guard that will be forgotten.
 *
 * ── Three states, never two (same discipline as `RoleProtectedRoute`) ──────────────────────────────
 *   - unresolved — the first config read for this user has not come back. This is NOT a denial.
 *                  A REAL page renders, because the alternative is a loading flash on every cold
 *                  load for a switch that is off for almost nobody. A DEMO route (the page, or the
 *                  module that owns it, carries the registry's demo marker) is held on a placeholder
 *                  instead and never renders in this state (CO-851): nothing is known about it yet,
 *                  and a demo page that paints before its OFF row arrives is the defect.
 *   - allowed    — the store says this page and its module are enabled, OR the URL is not in the
 *                  registry.
 *   - blocked    — the store says off. Only this state shows the notice and redirects.
 *
 * ── One read, shared (CO-851) ─────────────────────────────────────────────────────────────────────
 * The config comes from the navigation config store (`useNavConfig`), the same snapshot the sidebar
 * filters with, so the two cannot disagree. The store owns the fetch, the retry after a failure, and
 * the refetch on `ModuleConfigUpdated`, on a SignalR reconnect and on window focus. This guard used
 * to keep its own copy and its own subscription.
 *
 * ── Real pages fail OPEN, demo pages fail CLOSED ──────────────────────────────────────────────────
 * A URL absent from the registry is allowed: callbacks, redirects and detail views have no switch, and
 * blocking on a switch that does not exist would break working pages. For a real page an absent row
 * means enabled (matching the server default), and a failed read blocks nothing: a config outage must
 * not lock a tenant out. A demo page is the reverse: it is blocked unless a read succeeded, returned
 * rows and holds no OFF row for it, so one bad response can no longer open a fabricated screen. While
 * the read is failing the user gets the same notice and redirect as for a switched-off page; the
 * store keeps the last good rows across a failed refresh, so that only happens when nothing was ever
 * read for this session.
 *
 * ── Not a security boundary ───────────────────────────────────────────────────────────────────────
 * This withholds a SURFACE, not data. Every endpoint behind these pages keeps its own role and tenant
 * checks, and must: this guard is client-side and a determined caller simply skips it. Do not let a
 * page's switch stand in for an authorization check on its API.
 */
/**
 * 🔴 CO-500. Where a blocked page sends the user.
 *
 * This used to be an unconditional `<Navigate to="/" replace />`, which is a DEAD END whenever the
 * tenant has the `home` module switched off: `home` OWNS `path: '/'` (`navigationModules.ts`), so the
 * guard redirected to the very page it was blocking, blocked it again, and the content area stayed
 * blank forever. `HomeRoute` already has the correct fallback and it never ran, because this guard
 * wraps `MasterLayout`'s outlet and short-circuits before `children`.
 *
 * So resolve the target the way `HomeRoute` does, from the SAME filtered navigation:
 *   - `/` still in the filtered nav -> `/`, the dashboard. The previous behaviour, preserved exactly,
 *                                       for the overwhelmingly common case;
 *   - it is not                      -> the first page of the user's OWN filtered sidebar
 *                                       (`pickLandingPath`, which deliberately excludes `/` so it can
 *                                       never hand back a no-op);
 *   - nothing at all                 -> the user's OWN profile page (`selfProfilePath`), which this
 *                                       guard explicitly exempts so it is always reachable.
 *
 * 🔴 That last branch used to be justified as "the profile page, whose route has no guard". That was
 * true of `RoleProtectedRoute` and FALSE of this guard, which wraps MasterLayout's outlet globally --
 * `findNavEntryByPath` resolves `/profile/<id>/personal` to the `profile` module, so a tenant with
 * that module switched off was stranded on the blocked-page notice with nowhere to go (CO-502). The
 * exemption below makes the sentence true instead of merely assumed.
 *
 * FIRST TEST ASKS ABOUT THE PAGE, NOT THE MODULE, and that distinction is the whole point.
 * `HomeRoute` may key on the MODULE (`m.id === 'home'`) because it only decides what to RENDER. Here
 * the module is the wrong question: `home` also owns `/pending-actions`, so a tenant with the module
 * ON but the `/` PAGE switched off keeps `home` in the filtered list while `/` is still blocked -- and
 * a module-keyed test would send the user straight back into the dead end this function exists to
 * prevent. Prod has 10 `'/'` page rows with 0 disabled today, so that configuration is reachable
 * through the Operator UI but is not yet real anywhere.
 *
 * The invariant that matters: this NEVER returns a path this guard would itself block, because every
 * candidate comes from navigation that has already had module AND page config applied, and both of
 * those filters REMOVE rather than merely reorder.
 */
const resolveEscapePath = (modules: FilteredNavigationState['modules'], userId: string): string => {
  const rootPageSurvived = modules.some(
    m =>
      m.pages.some(pg => pg.path === '/') ||
      (m.subModules ?? []).some(sub => sub.pages.some(pg => pg.path === '/'))
  );
  if (rootPageSurvived) return '/';
  return pickLandingPath(modules) ?? selfProfilePath(userId);
};

export const PageConfigGuard = ({ children, navigation, forceBlocked = false }: PageConfigGuardProps) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const userId = useSelector((state: RootState) => state.user.userId);
  // CO-851: one shared read. The store binds itself to the signed-in user, refetches on the
  // ModuleConfigUpdated broadcast and retries a failed read; nothing is fetched or subscribed here.
  const navConfig = useNavConfig();
  const configStatus = navConfig.state.status;
  // A read has come back for THIS user, good or failed. Deliberately not `navConfig.settled`, which
  // is also true when nobody is signed in: with no user there is nothing to block on.
  const configResolved = configStatus === 'ready' || configStatus === 'failed';

  const entry = findNavEntryByPath(location.pathname);

  // For a real page an explicit `isEnabled: false` row is the ONLY thing that blocks; a missing row
  // means enabled. A demo page is also blocked while no usable read exists (CO-851).
  // CO-502: the last-resort escape must actually satisfy the invariant `resolveEscapePath` claims.
  // `findNavEntryByPath` resolves `/profile/<id>/personal` to the `profile` MODULE, so a tenant
  // holding an explicit `isEnabled:false` row for `profile` made this guard block its own escape --
  // CO-500's dead end at a new address. Never block a user's OWN profile page. The exemption is
  // keyed on the caller's own userId, so it cannot widen access to anyone else's profile, and
  // `RoleProtectedRoute` still applies on top.
  // CO-852: compared in normalised form. `findNavEntryByPath` now resolves a re-cased or re-encoded
  // URL, so `/Profile/<id>/Personal` reaches the `profile` module row too; a raw `===` here would
  // drop the exemption for exactly that spelling and strand the user again.
  const currentPath = normalizeNavPath(location.pathname);
  const isOwnLastResort = Boolean(userId) && currentPath === normalizeNavPath(selfProfilePath(userId));

  // CO-503: a page inside a SUB-module must also honour a module row keyed on the SUB-module id.
  // `findNavEntryByPath` used to hand back only the PARENT id, so a row on e.g. `impact-performance`
  // matched nothing and `/ai-impact-analysis` stayed reachable with the sub-module switched off
  // (runtime-proven by `G5` + `G5-CTL`, campaign-2026-08-29a). Testing BOTH ids is strictly additive:
  // it can only ever match an EXPLICIT `isEnabled:false` row, so a tenant with no config rows at all
  // — 10 of 20 live tenants (CO-496) — is unaffected and everything still defaults to ENABLED.
  const moduleIds = entry ? [entry.moduleId, ...(entry.subModuleId ? [entry.subModuleId] : [])] : [];

  // CO-815 review. `/attendance/verifyOvertime/<shiftId>` and `/attendance/verifyInBreak/<shiftId>` are
  // server-generated ACTION links (overtime / in-break verification jobs, push and email) on the caller's
  // OWN shift. They have no registry entry, so they resolve to the `/attendance` PAGE row, which the
  // Chief role overlay turns off by default: the verification never ran and the shift could be
  // auto-clocked-out. A PAGE row (company or role overlay) does not block them; a MODULE row switching
  // attendance off for the whole company still does. The verify endpoints keep their own server checks.
  const isShiftActionLink = /^\/attendance\/(verifyovertime|verifyinbreak)\/[^/]+\/?$/i.test(currentPath);

  // The store decides, per row kind. For a real page or module that is "an explicit OFF row"; for a
  // demo one it is also "no usable read yet" (see the store's rule in api/Navigation/moduleConfig.ts).
  const pageRowOff = entry !== undefined && !isShiftActionLink && !navConfig.isPageRowEnabled(entry.pagePath);
  const moduleRowOff = moduleIds.some(id => !navConfig.isModuleEnabled(id));

  const blocked =
    (forceBlocked && !isOwnLastResort) ||
    Boolean(configResolved && entry && !isOwnLastResort && (pageRowOff || moduleRowOff));

  // CO-851: a demo route waits for the first read instead of rendering. Same exemptions as `blocked`
  // (the own-profile escape, and the page row for a shift action link), so nothing that can never be
  // blocked is ever held either.
  const isDemoRoute =
    entry !== undefined &&
    ((!isShiftActionLink && isDemoPagePath(entry.pagePath)) || moduleIds.some(isDemoModuleId));
  const awaitingConfig = isLoggedIn && !configResolved && isDemoRoute && !isOwnLastResort && !blocked;

  const [redirect, setRedirect] = useState(false);

  useEffect(() => {
    if (!blocked) {
      setRedirect(false);
      return;
    }
    const timer = setTimeout(() => setRedirect(true), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [blocked, location.pathname]);

  if (awaitingConfig) {
    // Quiet on purpose: the read normally lands within the shell's own loading moment, and this is
    // not an error. No new copy; the status text is for assistive technology only.
    return (
      <div aria-busy="true" className="space-y-6" data-testid="page-config-pending">
        <span role="status" className="sr-only">{t('Loading...')}</span>
        <div className="space-y-2">
          <Skeleton className="h-7 w-48 max-w-full" />
          <Skeleton className="h-4 w-72 max-w-full" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!blocked) return <>{children}</>;

  // Redirecting before the filtered navigation has landed would resolve against defaults and move the
  // user twice. Holding the notice is the safe wait: it is already the visible state.
  if (redirect && navigation.loaded) {
    const escape = resolveEscapePath(navigation.modules, userId);
    // Guarding against the CO-500 shape itself: never navigate to the path we are blocking.
    if (escape !== location.pathname) return <Navigate to={escape} replace />;
  }

  return (
    <div role="alert" aria-live="assertive" className="flex flex-1 items-center justify-center p-6">
      <div className="flex max-w-sm flex-col items-center gap-3 rounded-xl border border-border bg-card px-6 py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <AppIcon name="EyeOff" size={24} />
        </div>
        <h2 className="text-base font-semibold text-foreground">
          {t('This page is turned off for your company')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('Redirecting you to your dashboard…')}
        </p>
        <button
          type="button"
          onClick={() => setRedirect(true)}
          className="mt-1 text-sm font-medium text-primary hover:underline"
        >
          {t('Go to dashboard')}
        </button>
      </div>
    </div>
  );
};

export default PageConfigGuard;
